import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { Box, Divider, Fab, Slider, Tooltip, Typography } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { ToggleButton, ToggleButtonGroup, IconButton } from "@mui/material";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import CloseIcon from "@mui/icons-material/Close";
import MapIcon from "@mui/icons-material/Map";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import HomeIcon from "@mui/icons-material/Home";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { MAP_STYLE } from "../constants";
import LegendBar from "./legendBar";

import styles from "./mapMenu.module.css";

const MapMenu = ({
  visibleMunicipalityValueType,
  setVisibleMunicipalityValueType,
  setActiveRisk,
  activeRisk,
  activeMapStyle,
  setActiveMapStyle,
  layerOpacities,
  setLayerOpacity,
  show3DMap,
  setShow3DMap,
  show3DBuildings,
  setShow3DBuildings,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const riskConfig = useMemo(
    () => ({
      fire: {
        title: t("portfolioMap.riskLegend.fireTitle"),
        colors: ["#2ECC71", "#F1C40F", "#E74C3C"],
        labels: [
          t("portfolioMap.riskLegend.low"),
          t("portfolioMap.riskLegend.medium"),
          t("portfolioMap.riskLegend.high"),
        ],
      },
      flood: {
        title: t("portfolioMap.riskLegend.floodTitle"),
        colors: ["#2ECC71", "#F1C40F", "#E74C3C"],
        labels: [
          t("portfolioMap.riskLegend.low"),
          t("portfolioMap.riskLegend.medium"),
          t("portfolioMap.riskLegend.high"),
        ],
      },
      earthquake: {
        title: t("portfolioMap.riskLegend.earthquakeTitle"),
        colors: ["#2ECC71", "#F1C40F", "#E74C3C"],
        labels: [
          t("portfolioMap.riskLegend.low"),
          t("portfolioMap.riskLegend.medium"),
          t("portfolioMap.riskLegend.high"),
        ],
      },
    }),
    [t],
  );

  return (
    <>
      <Fab
        aria-label={t("common.menu")}
        onClick={() => setOpen(!open)}
        className={styles.fab}
      >
        <MenuIcon />
      </Fab>

      {open && (
        <>
          <div className={styles.mapSettings}>
            <IconButton
              aria-label={t("common.hide")}
              onClick={() => setOpen(false)}
              className={styles.closeButton}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            <div>
              <Typography sx={{ mb: 1 }} variant="body2">
                {riskConfig[activeRisk].title}
              </Typography>

              {riskConfig[activeRisk].subtitle && (
                <Typography variant="body2">
                  {riskConfig[activeRisk].subtitle}
                </Typography>
              )}

              <LegendBar
                colors={riskConfig[activeRisk].colors}
                labels={riskConfig[activeRisk].labels}
              />
            </div>

            <Box sx={{ px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t("risksMap.layerOpacity")}
              </Typography>
              <Slider
                value={layerOpacities[activeRisk]}
                onChange={(_, value) => setLayerOpacity(activeRisk, value)}
                min={0}
                max={1}
                step={0.05}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => Math.round(v * 100) + "%"}
                size="small"
              />
            </Box>

            <Divider />

            <FormControl component="fieldset" variant="standard">
              <FormGroup
                sx={{
                  ml: "5px",
                  display: "flex",
                  gap: 1,
                  flexDirection: "row",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={show3DMap}
                      onChange={(_e, checked) => setShow3DMap(checked)}
                      name="3D-map"
                      size="small"
                    />
                  }
                  label={t("risksMap.show3d")}
                  componentsProps={{ typography: { variant: "body2" } }}
                />
                <Tooltip
                  title={
                    activeMapStyle === MAP_STYLE.satellite
                      ? t("risksMap.show3dBuildingsSatelliteDisabled")
                      : ""
                  }
                >
                  <span>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            activeMapStyle !== MAP_STYLE.satellite &&
                            show3DBuildings
                          }
                          onChange={(_e, checked) =>
                            setShow3DBuildings(checked)
                          }
                          name="3D-buildings"
                          size="small"
                          disabled={activeMapStyle === MAP_STYLE.satellite}
                        />
                      }
                      label={t("risksMap.show3dBuildings")}
                      componentsProps={{ typography: { variant: "body2" } }}
                    />
                  </span>
                </Tooltip>
              </FormGroup>
            </FormControl>

            <ToggleButtonGroup
              value={activeMapStyle}
              exclusive
              onChange={(e, value) => {
                if (value) {
                  setActiveMapStyle(value);
                }
              }}
              size="small"
              color="primary"
              sx={{ "& .MuiToggleButton-root": { fontSize: 14 } }}
            >
              <ToggleButton value={MAP_STYLE.street}>
                <MapIcon sx={{ mr: 1 }} fontSize="small" />
                {t("common.street")}
              </ToggleButton>
              <ToggleButton value={MAP_STYLE.satellite}>
                <SatelliteAltIcon sx={{ mr: 1 }} fontSize="small" />
                {t("common.satellite")}
              </ToggleButton>
            </ToggleButtonGroup>

            <FormControl component="fieldset" variant="standard">
              <FormLabel component="legend" sx={{ mb: "5px", fontSize: 14 }}>
                {t("portfolioMap.selectMunicipalityLabel")}
              </FormLabel>
              <ToggleButtonGroup
                value={visibleMunicipalityValueType}
                exclusive
                onChange={(e, value) => {
                  if (value) {
                    setVisibleMunicipalityValueType(value);
                  }
                }}
                size="small"
                color="primary"
                orientation="vertical"
                sx={{ "& .MuiToggleButton-root": { fontSize: 14 } }}
              >
                <ToggleButton
                  value="capital"
                  sx={{ justifyContent: "flex-start" }}
                >
                  <WarningAmberIcon fontSize="small" sx={{ mr: 1 }} />
                  {t("portfolioMap.capitalAtRisk")}
                </ToggleButton>
                <ToggleButton
                  value="price"
                  sx={{ justifyContent: "flex-start" }}
                >
                  <MonetizationOnIcon fontSize="small" sx={{ mr: 1 }} />
                  {t("portfolioMap.contractPrice")}
                </ToggleButton>
                <ToggleButton
                  value="count"
                  sx={{ justifyContent: "flex-start" }}
                >
                  <HomeIcon fontSize="small" sx={{ mr: 1 }} />
                  {t("portfolioMap.totalProperties")}
                </ToggleButton>
                <ToggleButton
                  value="none"
                  sx={{ justifyContent: "flex-start" }}
                >
                  <VisibilityOffIcon fontSize="small" sx={{ mr: 1 }} />
                  {t("common.hide")}
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>

            <FormControl component="fieldset" variant="standard">
              <FormLabel component="legend" sx={{ mb: "5px", fontSize: 14 }}>
                {t("risksMap.selectRiskMap")}
              </FormLabel>
              <ToggleButtonGroup
                value={activeRisk}
                exclusive
                onChange={(e, value) => {
                  if (value) {
                    setActiveRisk(value);
                  }
                }}
                size="small"
                color="primary"
                sx={{ "& .MuiToggleButton-root": { fontSize: 14 } }}
              >
                <ToggleButton value="fire">{t("risks.fire")}</ToggleButton>
                <ToggleButton value="flood">{t("risks.flood")}</ToggleButton>
                <ToggleButton value="earthquake">
                  {t("risks.earthquake")}
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          </div>
        </>
      )}
    </>
  );
};

export default MapMenu;
